import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Tag, Wrench, Phone, Sparkles } from 'lucide-react';
import { Button } from './ui/button';

export const PromoPopup = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [showFloatingIcon, setShowFloatingIcon] = useState(false);
  const [floatingIconDismissed, setFloatingIconDismissed] = useState(false);
  const location = useLocation();

  // Don't render on admin pages
  if (location.pathname.startsWith('/admin')) return null;

  useEffect(() => {
    // Check if floating icon was dismissed
    const iconDismissed = sessionStorage.getItem('specials_icon_dismissed');
    if (iconDismissed) {
      setFloatingIconDismissed(true);
    }

    // Don't show popup on mobile (< 768px)
    if (window.innerWidth < 768) return;
    
    // Check if popup was already shown in this session
    const popupShown = sessionStorage.getItem('promo_popup_shown');
    
    if (!popupShown) {
      // Show popup after a short delay
      const timer = setTimeout(() => {
        // Double-check width before showing
        if (window.innerWidth >= 768) {
          setIsOpen(true);
          sessionStorage.setItem('promo_popup_shown', 'true');
        }
      }, 3000);
      
      return () => clearTimeout(timer);
    } else {
      // Popup was already shown, show floating icon instead (if not dismissed)
      if (!iconDismissed) {
        setShowFloatingIcon(true);
      }
    }
  }, []);

  const handleClose = () => {
    setIsOpen(false);
    // Show floating icon after closing popup
    if (!floatingIconDismissed) {
      setShowFloatingIcon(true);
    }
  };

  const handleDismissFloatingIcon = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setShowFloatingIcon(false);
    setFloatingIconDismissed(true);
    sessionStorage.setItem('specials_icon_dismissed', 'true');
  };

  return (
    <>
      {/* Floating Specials Icon */}
      <AnimatePresence>
        {showFloatingIcon && !isOpen && (
          <motion.div
            initial={{ opacity: 0, scale: 0.8, x: 50 }}
            animate={{ opacity: 1, scale: 1, x: 0 }}
            exit={{ opacity: 0, scale: 0.8, x: 50 }}
            className="fixed bottom-8 right-4 z-40 hidden md:block"
            data-testid="floating-specials-icon"
          >
            <div className="relative">
              {/* Dismiss button */}
              <button
                onClick={handleDismissFloatingIcon}
                className="absolute -top-2 -right-2 bg-slate-700 hover:bg-slate-600 text-white rounded-full p-1 shadow-lg z-10"
                aria-label="Dismiss specials"
                data-testid="floating-specials-dismiss"
              >
                <X className="h-3 w-3" />
              </button>
              
              {/* Specials button */}
              <Link to="/specials">
                <motion.div
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="bg-gradient-to-r from-sky-500 to-sky-600 text-white px-4 py-3 rounded-full shadow-lg flex items-center gap-2 cursor-pointer"
                >
                  <Sparkles className="h-5 w-5" />
                  <span className="font-semibold">New Customer Specials</span>
                </motion.div>
              </Link>
              
              {/* Pulsing ring animation */}
              <div className="absolute inset-0 rounded-full bg-sky-500 animate-ping opacity-20 -z-10" />
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Main Popup */}
      <AnimatePresence>
        {isOpen && (
          <>
            {/* Backdrop - hidden on mobile */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/60 z-50 hidden md:block"
              onClick={handleClose}
            />
            
            {/* Popup container - scrollable, hidden on mobile */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 z-50 hidden md:flex items-center justify-center p-4 overflow-y-auto"
              onClick={handleClose}
            >
              {/* Popup content */}
              <motion.div
                initial={{ scale: 0.9, y: 20 }}
                animate={{ scale: 1, y: 0 }}
                exit={{ scale: 0.9, y: 20 }}
                transition={{ type: 'spring', damping: 25, stiffness: 300 }}
                className="w-full max-w-lg my-auto"
                data-testid="promo-popup"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="bg-white rounded-2xl shadow-2xl overflow-hidden">
                  {/* Header */}
                  <div className="bg-gradient-to-r from-sky-500 to-sky-600 px-5 py-4 relative">
                    <button
                      onClick={handleClose}
                      className="absolute top-3 right-3 p-1.5 hover:bg-white/20 rounded-full transition-colors text-white"
                      aria-label="Close popup"
                      data-testid="promo-popup-close"
                    >
                      <X className="h-5 w-5" />
                    </button>
                    <div className="flex items-center justify-center mb-1">
                      <Tag className="h-6 w-6 text-white mr-2" />
                      <h2 className="text-xl sm:text-2xl font-bold text-white text-center">
                        New Customer Specials!
                      </h2>
                    </div>
                    <p className="text-white/90 text-center text-sm">
                      Limited time offers for new customers
                    </p>
                  </div>

                  {/* Content */}
                  <div className="p-5">
                    <div className="grid md:grid-cols-2 gap-4">
                      {/* Offer 1 */}
                      <div className="bg-slate-50 rounded-xl p-4 border-2 border-slate-100 hover:border-sky-500 transition-colors">
                        <div className="flex items-center mb-3">
                          <div className="p-1.5 bg-sky-500 rounded-lg mr-2">
                            <Tag className="h-4 w-4 text-white" />
                          </div>
                          <h3 className="font-bold text-slate-900 text-base">
                            10% Off First Month
                          </h3>
                        </div>
                        <p className="text-slate-600 text-sm mb-3">
                          New customers mention code <span className="font-bold text-sky-600">"ONWEB"</span> for 10% off your first month's cleaning service.
                        </p>
                        <ul className="space-y-1 text-xs text-slate-600 mb-2">
                          <li>• Bi-Full or Full Service Cleaning</li>
                          <li>• Credit applied 3rd month</li>
                        </ul>
                        <p className="text-xs text-slate-400">
                          *Call for details.
                        </p>
                      </div>

                      {/* Offer 2 */}
                      <div className="bg-slate-50 rounded-xl p-4 border-2 border-slate-100 hover:border-sky-500 transition-colors">
                        <div className="flex items-center mb-3">
                          <div className="p-1.5 bg-sky-500 rounded-lg mr-2">
                            <Wrench className="h-4 w-4 text-white" />
                          </div>
                          <h3 className="font-bold text-slate-900 text-base">
                            Equipment Discounts
                          </h3>
                        </div>
                        <p className="text-slate-600 text-sm mb-3">
                          Save on repairs and new equipment!
                        </p>
                        <ul className="space-y-1 text-xs text-slate-600 mb-2">
                          <li>• <span className="font-semibold">$10 off</span> repairs up to $150</li>
                          <li>• <span className="font-semibold">$20 off</span> repairs up to $250</li>
                          <li>• <span className="font-semibold">Up to $35 off</span> new equipment over $500</li>
                        </ul>
                        <p className="text-xs text-slate-400">
                          *Minimum $99. Restrictions apply.
                        </p>
                      </div>
                    </div>

                    {/* CTA Buttons */}
                    <div className="mt-5 flex flex-col sm:flex-row gap-3 justify-center">
                      <Button
                        asChild
                        className="bg-sky-500 hover:bg-sky-400 text-white rounded-full px-6 py-5"
                        data-testid="promo-popup-cta"
                      >
                        <Link to="/specials" onClick={handleClose}>
                          View All Specials
                        </Link>
                      </Button>
                      <Button
                        asChild
                        variant="outline"
                        className="border-2 border-slate-300 text-slate-700 hover:bg-slate-100 rounded-full px-6 py-5"
                      >
                        <a href="tel:813-453-5988">
                          <Phone className="mr-2 h-4 w-4" />
                          813-453-5988
                        </a>
                      </Button>
                    </div>

                    <p className="text-center text-xs text-slate-400 mt-3">
                      Cannot be combined with other offers.
                    </p>
                  </div>
                </div>
              </motion.div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </>
  );
};
